import { lazy, Suspense, useState, useCallback } from 'react'
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { MvpSidecarMenu } from './components/layout/MvpSidecarMenu'
import { MvpQuickNav } from './components/layout/MvpQuickNav'
import { PageTransition } from './components/layout/PageTransition'
import { Footer } from './components/layout/Footer'
import { ThemeProvider, useTheme } from './contexts/ThemeContext'
import { AuthProvider } from './context/AuthContext'
import { I18nProvider, useI18n } from './i18n/I18nContext'
import { WelcomeModal } from './components/features/WelcomeModal'
import type { WelcomeData } from './components/features/WelcomeModal'
import { mockUser } from './data/mockData'
import './styles/quick-nav.css'
import './styles/filters.css'
import './styles/commissions.css'

const Commissions = lazy(() => import('./pages/CommissionsNew'))
const Dashboard = lazy(() => import('./pages/DashboardNew'))
const MyBusiness = lazy(() => import('./pages/MyBusiness'))
const BookOfBusiness = lazy(() => import('./pages/BookOfBusinessNew'))
const PointsDetail = lazy(() => import('./pages/PointsDetailNew'))
const PolicyDetail = lazy(() => import('./pages/PolicyDetail'))
const AgentDetail = lazy(() => import('./pages/AgentDetail'))
const TeamSummary = lazy(() => import('./pages/TeamSummary'))
const HierarchyVisualization = lazy(() => import('./pages/HierarchyVisualization'))
const WidgetConfig = lazy(() => import('./pages/WidgetConfig'))
const WidgetLibraryPage = lazy(() => import('./pages/WidgetLibraryPage'))
const MyWFGDemo = lazy(() => import('./pages/MyWFGDemo'))
const ReportGenerator = lazy(() => import('./pages/ReportGenerator'))
const HeroCardLarge = lazy(() => import('./pages/HeroCardLarge'))
const AgentPageManager = lazy(() => import('./pages/AgentPageManager'))
const AgentPagePreview = lazy(() => import('./pages/AgentPagePreview'))
const NotFound = lazy(() => import('./pages/NotFound'))

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>
  )
}

const mvpNavItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    id: 'my-business',
    label: 'My Business',
    path: '/my-business',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
  },
  {
    id: 'commissions',
    label: 'Commissions',
    path: '/commissions',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: 'book-of-business',
    label: 'Book of Business',
    path: '/book-of-business',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    id: 'points',
    label: 'Points',
    path: '/points',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>
    ),
  },
  {
    id: 'team',
    label: 'Team Summary',
    path: '/team',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    id: 'hierarchy',
    label: 'Hierarchy',
    path: '/hierarchy',
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
  },
]

function AppLayout() {
  const [sidecarOpen, setSidecarOpen] = useState(false)
  const { effectiveTheme, setTheme } = useTheme()
  const location = useLocation()
  const i18n = useI18n()

  // Welcome modal — show once per browser
  const [showWelcome, setShowWelcome] = useState(() => !localStorage.getItem('mvp-welcome-done'))

  const handleWelcomeComplete = useCallback((data: WelcomeData) => {
    localStorage.setItem('mvp-welcome-done', 'true')
    localStorage.setItem('mvp-profile', JSON.stringify(data))
    // Update locale if French was chosen
    if (data.language === 'fr') {
      const { setLocale } = i18n
      setLocale('fr')
    }
    setShowWelcome(false)
  }, [])

  const handleThemeToggle = useCallback((isDark: boolean) => {
    setTheme(isDark ? 'dark' : 'light')
  }, [setTheme])

  const toggleSidecar = useCallback(() => {
    setSidecarOpen(prev => !prev)
  }, [])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-900 flex flex-col">
      <WelcomeModal isOpen={showWelcome} onComplete={handleWelcomeComplete} />
      <MvpQuickNav onMenuClick={toggleSidecar} />

      <MvpSidecarMenu
        isOpen={sidecarOpen}
        onToggle={toggleSidecar}
        user={mockUser}
        navItems={mvpNavItems}
        onThemeToggle={handleThemeToggle}
        isDarkMode={effectiveTheme === 'dark'}
      />

      <main className="py-6 flex-1">
        <Suspense fallback={<LoadingFallback />}>
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              {/* Default: redirect to dashboard */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<PageTransition><Dashboard /></PageTransition>} />
              <Route path="/my-business" element={<PageTransition><MyBusiness /></PageTransition>} />
              <Route path="/commissions" element={<PageTransition><Commissions /></PageTransition>} />
              <Route path="/book-of-business" element={<PageTransition><BookOfBusiness /></PageTransition>} />
              <Route path="/book-of-business/policy/:policyId" element={<PageTransition><PolicyDetail /></PageTransition>} />
              <Route path="/points" element={<PageTransition><PointsDetail /></PageTransition>} />
              <Route path="/team" element={<PageTransition><TeamSummary /></PageTransition>} />
              <Route path="/team/agent/:agentId" element={<PageTransition><AgentDetail /></PageTransition>} />
              <Route path="/hierarchy" element={<PageTransition><HierarchyVisualization /></PageTransition>} />
              <Route path="/widget-config" element={<PageTransition><WidgetConfig /></PageTransition>} />
              <Route path="/widgets" element={<PageTransition><WidgetLibraryPage /></PageTransition>} />
              <Route path="/mywfg-demo" element={<PageTransition><MyWFGDemo /></PageTransition>} />
              <Route path="/reports" element={<PageTransition><ReportGenerator /></PageTransition>} />
              <Route path="/hero-card" element={<PageTransition><HeroCardLarge /></PageTransition>} />
              <Route path="/agent-pages" element={<PageTransition><AgentPageManager /></PageTransition>} />
              <Route path="/agent-pages/preview/:pageId" element={<PageTransition><AgentPagePreview /></PageTransition>} />
              <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
            </Routes>
          </AnimatePresence>
        </Suspense>
      </main>

      <Footer />
    </div>
  )
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <I18nProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AppLayout />
          </Router>
        </I18nProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
